import React, { useState, useEffect, useCallback } from 'react';

interface TradeCoinData {
  id: number;
  code: string;
  trade_price: string;
  change_rate: string;
  trade_volume: string;
  collected_at: string;
  trade_timestamp?: string;
  created_at: string;
}

const TradeCoin: React.FC = () => {
  const [data, setData] = useState<TradeCoinData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/mercury-backend/bt/trade-coin?limit=${limit}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data || []);
      } else {
        throw new Error(result.error || '데이터 조회 실패');
      }
    } catch (err) {
      console.error('Trade coin 데이터 조회 오류:', err);
      setError(err instanceof Error ? err.message : '알 수 없는 오류');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchData();
    // 5초마다 자동 갱신
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getChangeRateColor = (changeRate: string) => {
    const rate = parseFloat(changeRate);
    if (rate > 0) return '#ff4444'; // 상승: 빨간색
    if (rate < 0) return '#4444ff'; // 하락: 파란색
    return '#ffffff'; // 보합: 흰색
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '10px'
      }}>
        <div>
          <label style={{ color: '#ffffff', fontSize: '12px', marginRight: '10px' }}>
            표시 개수:
          </label>
          <input
            type="number"
            value={limit}
            onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 100))}
            style={{
              padding: '4px 8px',
              border: '1px solid #444',
              background: '#2a2a2a',
              color: '#fff',
              fontSize: '12px',
              borderRadius: '4px',
              width: '80px'
            }}
            min="1"
            max="1000"
          />
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            padding: '6px 12px',
            border: '1px solid #444',
            background: loading ? '#444' : '#2a2a2a',
            color: '#fff',
            fontSize: '12px',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? '로딩 중...' : '새로고침'}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '10px',
          background: '#cc0000',
          color: '#fff',
          borderRadius: '4px',
          marginBottom: '10px',
          fontSize: '12px'
        }}>
          오류: {error}
        </div>
      )}

      <div style={{
        overflowX: 'auto',
        maxHeight: '600px',
        overflowY: 'auto'
      }}>
        <table className="table" style={{ width: '100%', fontSize: '11px' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>코인 코드</th>
              <th>거래가격</th>
              <th>등락률</th>
              <th>거래량</th>
              <th>수집 시간</th>
              <th>거래 타임스탬프</th>
              <th>생성 시간</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                  데이터가 없습니다
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td style={{ fontWeight: 'bold' }}>{item.code}</td>
                  <td>{parseFloat(item.trade_price).toLocaleString()}</td>
                  <td style={{ color: getChangeRateColor(item.change_rate) }}>
                    {parseFloat(item.change_rate).toFixed(2)}%
                  </td>
                  <td>{parseFloat(item.trade_volume).toLocaleString()}</td>
                  <td>{new Date(item.collected_at).toLocaleString('ko-KR')}</td>
                  <td>{item.trade_timestamp || '-'}</td>
                  <td>{new Date(item.created_at).toLocaleString('ko-KR')}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: '#888',
          fontSize: '12px'
        }}>
          데이터 로딩 중...
        </div>
      )}
    </div>
  );
};

export default TradeCoin;



